import { get, isEmpty, isEqual } from 'lodash';
import { checkDistanceBetweenCoordinates, getDeviceType, isDesktopPlatform } from '../../../utils/helpers';
import { getItem, setItem } from '../../../utils/storage';
import { orderModes, storageKeys, storageTypes } from '../../../constants/globals';

export const SITE_OUTLET_SELECTION_SOURCE = {
  auto: 'auto',
  manual: 'manual',
};

export const getOutletDisplayName = (outlet = {}) => {
  const displayName = get(outlet, 'displayName', '');
  if (!isEmpty(displayName)) {
    return displayName;
  }
  return get(outlet, 'name', '') || '';
};

export const getDeliveryLocationLabel = (outlet = {}) => {
  const displayName = get(outlet, 'displayName', '');
  if (!isEmpty(displayName)) {
    return displayName;
  }
  const city =
    get(outlet, 'city', '') || get(outlet, 'resCity.name', '') || get(outlet, 'cityName', '');
  if (!isEmpty(city)) {
    return city;
  }
  return get(outlet, 'name', '') || '';
};

export const normalizeGuestLocation = (coords) => {
  if (isEmpty(coords)) return null;

  const lat = get(coords, 'lat', get(coords, 'latitude', ''));
  const lng = get(coords, 'lng', get(coords, 'longitude', ''));

  if (lat === '' || lng === '' || lat == null || lng == null) {
    return null;
  }

  const parsedLat = Number(lat);
  const parsedLng = Number(lng);

  if (Number.isNaN(parsedLat) || Number.isNaN(parsedLng)) {
    return null;
  }

  return { lat: parsedLat, lng: parsedLng };
};

export const findNearestOutlet = ({ outlets, guestLocation }) => {
  const normalizedLocation = normalizeGuestLocation(guestLocation);
  if (isEmpty(normalizedLocation) || isEmpty(outlets)) {
    return { outlet: {} };
  }

  const nearest = outlets.reduce((currentNearest, outlet) => {
    const latitude2 = get(outlet, 'restaurantConfig.latitude');
    const longitude2 = get(outlet, 'restaurantConfig.longitude');

    if (latitude2 == null || longitude2 == null) {
      return currentNearest;
    }

    const distance = checkDistanceBetweenCoordinates(
      {
        latitude1: normalizedLocation.lat,
        longitude1: normalizedLocation.lng,
        latitude2,
        longitude2,
      },
      false,
    );

    if (!currentNearest || distance < currentNearest.distance) {
      return { outlet, distance };
    }

    return currentNearest;
  }, null);

  return nearest || { outlet: {} };
};

export const filterServiceTypesByApplicableModes = (serviceTypes, applicableOrderModes) => {
  if (isEmpty(serviceTypes)) {
    return [];
  }

  if (isEmpty(applicableOrderModes)) {
    return serviceTypes;
  }

  return serviceTypes.filter((serviceType) =>
    applicableOrderModes.includes(get(serviceType, 'code')),
  );
};

export const resolveOutletBindService = ({
  serviceTypes,
  applicableOrderModes,
  preferredMode,
}) => {
  const modes = filterServiceTypesByApplicableModes(serviceTypes, applicableOrderModes);
  if (preferredMode) {
    const preferred = modes.find((serviceType) => get(serviceType, 'code') === preferredMode);
    if (!isEmpty(get(preferred, 'scanCode'))) {
      return { autoApply: preferred, modes };
    }
  }
  if (modes.length === 1 && !isEmpty(get(modes[0], 'scanCode'))) {
    return { autoApply: modes[0], modes };
  }
  return { autoApply: null, modes };
};

/**
 * Site dine-in / room cannot proceed like pickup/delivery.
 * @returns {'desktopUnavailable'|'scanQr'|null}
 */
export const getSiteDineInOrRoomPresentation = (service) => {
  const code = get(service, 'code', '');
  if (!isEqual(code, orderModes.dineIn) && !isEqual(code, orderModes.room)) {
    return null;
  }
  const deviceType = getDeviceType();
  const isDesktopDevice = isDesktopPlatform() || isEqual(deviceType, 'desktop');
  return isDesktopDevice ? 'desktopUnavailable' : 'scanQr';
};

export const resolveFirstServiceScanCode = ({
  serviceTypes,
  applicableOrderModes,
  fallbackScanCode,
}) => {
  const filteredServiceTypes = filterServiceTypesByApplicableModes(
    serviceTypes,
    applicableOrderModes,
  );
  const firstService = filteredServiceTypes[0] || serviceTypes[0];
  const scanCode = get(firstService, 'scanCode', '');

  if (!isEmpty(scanCode)) {
    return scanCode;
  }

  return fallbackScanCode || '';
};

export const getSiteOutletSession = () => ({
  autoSelected: getItem(storageTypes.sessionStorage, storageKeys.siteOutletAutoSelected) === true,
  selectionSource: getItem(storageTypes.sessionStorage, storageKeys.siteOutletSelectionSource, ''),
  selectedScanCode: getItem(storageTypes.sessionStorage, storageKeys.siteSelectedScanCode, ''),
});

export const setSiteOutletSession = ({ scanCode, source }) => {
  if (!isEmpty(scanCode)) {
    setItem(storageTypes.sessionStorage, storageKeys.siteSelectedScanCode, scanCode);
  }
  if (!isEmpty(source)) {
    setItem(storageTypes.sessionStorage, storageKeys.siteOutletSelectionSource, source);
  }
  if (source === SITE_OUTLET_SELECTION_SOURCE.auto) {
    setItem(storageTypes.sessionStorage, storageKeys.siteOutletAutoSelected, true);
  } else if (source === SITE_OUTLET_SELECTION_SOURCE.manual) {
    // Manual pick must clear any prior auto flag or bootstrap will wipe the table.
    setItem(storageTypes.sessionStorage, storageKeys.siteOutletAutoSelected, false);
  }
};

export const markSiteOutletManualSelection = (scanCode = '') => {
  setSiteOutletSession({
    scanCode,
    source: SITE_OUTLET_SELECTION_SOURCE.manual,
  });
};

export const shouldRunSiteOutletAutoSelect = () => {
  const session = getSiteOutletSession();
  return (
    session.selectionSource !== SITE_OUTLET_SELECTION_SOURCE.manual && !session.autoSelected
  );
};
